import { Flex, Text } from '@chakra-ui/react'
import { memo } from 'react'

import type { MessageMessageFragment } from '@/client/services/hasura/generated/graphql'

import MessageEmbedV1, {
  MESSAGE_EMBED_TYPE,
} from '@/components/Conversation/MessageEmbedV1/MessageEmbedV1'
import { withErrorBoundary } from '@/components/Worlds/UI/ErrorBoundary'

import MessageOperatorV1 from './MessageOperatorV1'
import MessagePlainV1 from './MessagePlainV1'

function isOperatorMessage(message: MessageMessageFragment): message is MessageMessageFragment & {
  type: 'operator-plain-v1' | 'operator-announcement-v1'
} {
  return message.type === 'operator-plain-v1' || message.type === 'operator-announcement-v1'
}

function MessageNotMemoized({
  message,
  lastMessage,
  isAdminView = false,
}: {
  message: MessageMessageFragment
  lastMessage: MessageMessageFragment | null
  isAdminView?: boolean
}): React.ReactElement {
  if (isOperatorMessage(message)) {
    return <MessageOperatorV1 message={message} />
  }

  if (message.type === MESSAGE_EMBED_TYPE) {
    return <MessageEmbedV1 message={message} />
  }

  return <MessagePlainV1 message={message} lastMessage={lastMessage} isAdminView={isAdminView} />
}

// Memoize this, so that changing one input doesn't affect the rendering
// of all the other inputs.
const Message = memo(
  withErrorBoundary(MessageNotMemoized, () => (
    <Flex flexDir="row" justifyContent="center" py="8px" color="fg.muted">
      <Text as="i" color="fg.subtle">
        Oops! There was an error displaying this message.
      </Text>
    </Flex>
  )),
  (prevProps, newProps) =>
    prevProps.message.id === newProps.message.id &&
    prevProps.lastMessage?.id === newProps.lastMessage?.id &&
    `${prevProps.message.updated_at}` === `${newProps.message.updated_at}`
)

export default Message
